import { FeedbackLine } from "./FeedbackLine";
import { useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button, FileButton, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconBrandGoogleFilled } from "@tabler/icons-react";
import { ApiError } from "../api/client";
import {
  useClearAvatar,
  useIdentities,
  useMe,
  usePublicSettings,
  useUnlinkIdentity,
  useUpdateProfile,
  useUploadAvatar,
} from "../api/queries";
import { DEFAULT_AVATAR_URL, displayNameOf } from "../api/types";
import { usePrefs } from "../state/prefs";
import type { Lang } from "../i18n";
import { nativeRuntime } from "../platform/native";
import { handleNativeLink } from "../platform/nativeLinks";
import { useLanguage, useSetLanguage } from "../i18n/useLanguage";
import type { ZoneMode } from "../time/format";
import { useZone } from "../time/useZone";
import { Artwork } from "./Artwork";
import { pickNativeImage } from "./nativeImagePicker";
import { Chip } from "./Chip";

/** Catalogue keys, not copy: a module-level constant would freeze the language at import. */
const ZONE_OPTION_KEYS = [
  { value: "local", labelKey: "settingsDialog.zoneLocal" },
  { value: "station", labelKey: "settingsDialog.zoneStation" },
  { value: "utc", labelKey: "settingsDialog.zoneUtc" },
] as const satisfies readonly { value: ZoneMode; labelKey: string }[];

/** Matches MAX_AVATAR_BYTES in api/artwork.py. The server is the real check; this only
 *  avoids spending a phone's uplink on a file that is going to be refused. */
export const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

/** Why this upload will not be attempted, or null to go ahead. */
export function avatarRejection(bytes: number, t: TFunction): string | null {
  if (bytes > MAX_AVATAR_BYTES) {
    return t("settingsDialog.avatarTooBig", { kb: Math.round(bytes / 1024) });
  }
  return null;
}

/**
 * The zone the whole UI reads timestamps in.
 *
 * Lives in user settings, reachable whether or not there is an account: it is read once a
 * session and was competing with the player for the header's width. Signed out it used to sit
 * loose in the row, which put the same control in two places depending on state -- a visitor
 * changing the zone and a member changing it are the same act, so it is one control in one
 * place.
 */
function ZoneRow() {
  const { t } = useTranslation();
  const zone = useZone();
  const setZoneMode = usePrefs((s) => s.setZoneMode);
  const options = ZONE_OPTION_KEYS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
  return (
    <Group gap={6} fz={12} c="dimmed">
      <Select
        value={zone.mode}
        data={options}
        aria-label={t("settingsDialog.timezone")}
        title={zone.mode === "station" ? zone.station : undefined}
        onChange={(mode) => mode && setZoneMode(mode as ZoneMode)}
      />
      <Text component="span" fz={11} c="dimmed" ml={6}>
        {zone.short}
      </Text>
    </Group>
  );
}

const LANGUAGE_OPTIONS: readonly { value: Lang; label: string }[] = [
  // Endonyms, never translated: someone stuck in a language they cannot read must still be
  // able to find their own.
  { value: "en", label: "English" },
  { value: "ru", label: "Русский" },
];

function LanguageRow() {
  const { t } = useTranslation();
  const language = useLanguage();
  const setLanguage = useSetLanguage();
  return (
    <Select
      value={language}
      data={LANGUAGE_OPTIONS}
      aria-label={t("settingsDialog.language")}
      onChange={(next) => next && setLanguage(next as Lang)}
    />
  );
}

export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const clearAvatar = useClearAvatar();
  const settings = usePublicSettings();
  const identities = useIdentities(!!me);
  const unlink = useUnlinkIdentity();
  // The only field here that waits for a Save. It carries no validation rule: an empty name
  // is legal and falls back to the username, which is what the placeholder says.
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { display_name: me?.display_name ?? "" },
  });
  // The one rejection the server never sees, so the one this component has to word itself.
  const runtime = nativeRuntime();
  const [tooBig, setTooBig] = useState<string | null>(null);
  const [pickError, setPickError] = useState<unknown>(null);
  // Re-picking the same file fires no `change` unless the input is cleared first.
  const resetFile = useRef<() => void>(null);
  // Either mutation writes the same picture, so one line reports whichever last refused.
  const avatarError = pickError ?? uploadAvatar.error ?? clearAvatar.error;

  return (
    <Modal opened onClose={onClose} title={t("settingsDialog.heading")}>
      <Stack gap="md">
        {/* Always shown. The zone is a fact about how this browser reads the page, not about
            an account, so signing out must not take the control away with it. */}
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {t("settingsDialog.timezone")}
          </Text>
          <ZoneRow />
        </Stack>

        {/* Same reasoning as the zone above: which language this browser reads the page in
            is not a property of an account. */}
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {t("settingsDialog.language")}
          </Text>
          <LanguageRow />
        </Stack>

        {me && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              {t("settingsDialog.displayName")}
            </Text>
            <form onSubmit={form.onSubmit((values) => updateProfile.mutate(values))}>
              <Stack gap="xs">
                <TextInput
                  data-autofocus
                  name="display_name"
                  maxLength={64}
                  placeholder={me.username}
                  description={t("settingsDialog.nameHint", { username: me.username })}
                  key={form.key("display_name")}
                  {...form.getInputProps("display_name")}
                />
                <FeedbackLine role="alert">
                  {updateProfile.error &&
                    (updateProfile.error instanceof ApiError
                      ? updateProfile.error.detail
                      : String(updateProfile.error))}
                </FeedbackLine>
                <Group justify="flex-end" gap="xs">
                  <Chip type="submit" variant="accent" disabled={updateProfile.isPending}>
                    {updateProfile.isPending ? t("common.saving") : t("common.save")}
                  </Chip>
                </Group>
              </Stack>
            </form>
          </Stack>
        )}

        {me && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              {t("settingsDialog.avatar")}
            </Text>
            <Group gap="sm">
              <Artwork
                src={me.avatar_url ?? DEFAULT_AVATAR_URL}
                seed={displayNameOf(me)}
                size={64}
                // A circle rather than the tile's rounded square: a face is not a sleeve.
                radius="50%"
                alt={t("settingsDialog.avatarAlt", { name: displayNameOf(me) })}
              />
              {runtime ? (
                <Button
                  disabled={uploadAvatar.isPending}
                  onClick={() => {
                    setPickError(null);
                    void pickNativeImage(
                      runtime,
                      (bytes) => avatarRejection(bytes, t),
                      (file) => uploadAvatar.mutate(file),
                    )
                      .then(setTooBig)
                      .catch(setPickError);
                  }}
                >
                  {t("common.chooseFile")}
                </Button>
              ) : (
                <FileButton
                  resetRef={resetFile}
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(file) => {
                    resetFile.current?.();
                    setPickError(null);
                    if (!file) return;
                    const rejection = avatarRejection(file.size, t);
                    setTooBig(rejection);
                    if (rejection === null) uploadAvatar.mutate(file);
                  }}
                >
                  {(props) => <Button {...props}>{t("common.chooseFile")}</Button>}
                </FileButton>
              )}
              {me.avatar_url && (
                <Chip
                  onClick={() => {
                    // A refused pick's complaint is about a file, not about the avatar;
                    // leaving it up after a successful removal reads as a failed removal.
                    setTooBig(null);
                    clearAvatar.mutate();
                  }}
                >
                  {t("settingsDialog.removeAvatar")}
                </Chip>
              )}
            </Group>
            <Text c="dimmed" size="sm">
              {t("settingsDialog.avatarHint")}
            </Text>
            <FeedbackLine role="alert">
              {(tooBig || avatarError) &&
                (tooBig ??
                  (avatarError instanceof ApiError ? avatarError.detail : String(avatarError)))}
            </FeedbackLine>
          </Stack>
        )}

        {me && (settings.data?.sso_providers ?? []).length > 0 && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              {t("settingsDialog.connectedAccounts")}
            </Text>
            {(settings.data?.sso_providers ?? []).map((name) => {
              const linked = identities.data?.find((item) => item.provider === name);
              return linked ? (
                <Group key={name} gap="sm" justify="space-between">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    {name === "google" && (
                      <IconBrandGoogleFilled
                        size={20}
                        role="img"
                        aria-label={t(`sso.${name}`)}
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <Text size="sm" style={{ overflowWrap: "anywhere", minWidth: 0 }}>
                      {linked.email ?? t(`sso.${name}`)}
                    </Text>
                  </Group>
                  <Chip onClick={() => unlink.mutate(name)} disabled={unlink.isPending}>
                    {t("settingsDialog.unlink")}
                  </Chip>
                </Group>
              ) : (
                // Same plain link as the login dialog; a live session turns the callback
                // into a link against this account rather than a sign-in.
                <Button
                  key={name}
                  component="a"
                  href={`/api/auth/sso/${name}/start`}
                  variant="default"
                  leftSection={
                    name === "google" ? (
                      <IconBrandGoogleFilled size={20} aria-hidden="true" />
                    ) : undefined
                  }
                  onClick={(event) =>
                    void handleNativeLink(event, runtime, (native) =>
                      native.signIn("account", name),
                    )
                  }
                >
                  {t("settingsDialog.link", { provider: t(`sso.${name}`) })}
                </Button>
              );
            })}
            <FeedbackLine role="alert">
              {unlink.error &&
                (unlink.error instanceof ApiError ? unlink.error.detail : String(unlink.error))}
            </FeedbackLine>
          </Stack>
        )}

        {/* No Cancel/OK pair: every control here has already committed by the time it is
            read. */}
        <Group justify="flex-end" gap="xs">
          <Chip onClick={onClose}>{t("common.close")}</Chip>
        </Group>
      </Stack>
    </Modal>
  );
}
